import React, { useEffect, useState } from 'react';
import {
  Alert,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Space,
  Typography,
} from 'antd';
import { useCampaignRequestorContext } from '../../../core/components/campaignRequestor/useCampaignRequestorContext';
import moment from 'moment';
import { usePermify } from '@permify/react-role';

const { Text } = Typography;

const CampaignRequestorBudget = ({ formValues, derivedState, helpers }) => {
  const { currentUserOrg, currentCampaignOrder } = useCampaignRequestorContext();
  const { isAuthorized } = usePermify();
  const [canEdit, setCanEdit] = useState(false);

  // Extract reactive values
  const { advertiserId, startDate, endDate, budget, cpm } = formValues;

  const handleStartDateChange = (value) => {
    let updatedEndDate = endDate;
    let updatedStartDate = value;

    if (value) {
      if (value.isAfter(endDate)) {
        updatedEndDate = null;
      }
    }

    // Update start date using helper
    helpers.updateField('startDate', updatedStartDate);

    // Check if end date is set and is greater than 2 days after start date
    if (endDate && moment(endDate).isAfter(moment(value).add(2, 'days'))) {
      const numberOfDays = updatedEndDate ? updatedEndDate.diff(updatedStartDate, 'days') + 1 : 3;

      // Get current campaigns to update their dates
      const currentCampaigns = helpers.getFieldValue('campaigns') || [];
      const updatedCampaigns = currentCampaigns.map((campaign, index) => {
        if (index === 0) {
          return {
            ...campaign,
            startDate: updatedStartDate,
            endDate: updatedEndDate,
          };
        }
        return campaign;
      });

      helpers.setFieldsValue({
        startDate: updatedStartDate,
        endDate: updatedEndDate,
        numberOfDays,
        campaigns: updatedCampaigns,
      });
      return;
    }

    // Set end date 2 days after start date
    if (value) {
      updatedEndDate = moment(value).add(2, 'days');
      const numberOfDays = updatedEndDate.diff(updatedStartDate, 'days') + 1;

      // Get current campaigns to update their dates
      const currentCampaigns = helpers.getFieldValue('campaigns') || [];
      const updatedCampaigns = currentCampaigns.map((campaign, index) => {
        if (index === 0) {
          return {
            ...campaign,
            startDate: updatedStartDate,
            endDate: updatedEndDate,
          };
        }
        return campaign;
      });

      helpers.setFieldsValue({
        startDate: updatedStartDate,
        endDate: updatedEndDate,
        numberOfDays,
        campaigns: updatedCampaigns,
      });
    }
  };

  const handleEndDateChange = (value) => {
    let updatedEndDate = value;
    let updatedStartDate = startDate;

    if (value) {
      if (value.isBefore(startDate)) {
        updatedStartDate = null;
      }
    }

    // Update end date using helper
    helpers.updateField('endDate', updatedEndDate);

    // Check if start date is set and end date is greater than 2 days after start date
    if (startDate && moment(value).isAfter(moment(startDate).add(2, 'days'))) {
      const numberOfDays = updatedEndDate ? updatedEndDate.diff(updatedStartDate, 'days') + 1 : 3;

      // Get current campaigns to update their dates
      const currentCampaigns = helpers.getFieldValue('campaigns') || [];
      const updatedCampaigns = currentCampaigns.map((campaign, index) => {
        if (index === 0) {
          return {
            ...campaign,
            startDate: updatedStartDate,
            endDate: updatedEndDate,
          };
        }
        return campaign;
      });

      helpers.setFieldsValue({
        startDate: updatedStartDate,
        endDate: updatedEndDate,
        numberOfDays,
        campaigns: updatedCampaigns,
      });
      return;
    }

    // Set start date 2 days before end date
    if (value) {
      updatedStartDate = moment(value).subtract(2, 'days');
      const numberOfDays = updatedEndDate.diff(updatedStartDate, 'days') + 1;

      // Get current campaigns to update their dates
      const currentCampaigns = helpers.getFieldValue('campaigns') || [];
      const updatedCampaigns = currentCampaigns.map((campaign, index) => {
        if (index === 0) {
          return {
            ...campaign,
            startDate: updatedStartDate,
            endDate: updatedEndDate,
          };
        }
        return campaign;
      });

      helpers.setFieldsValue({
        startDate: updatedStartDate,
        endDate: updatedEndDate,
        numberOfDays,
        campaigns: updatedCampaigns,
      });
    }
  };

  const disabledEndDate = (date) => {
    if (!startDate) {
      return true;
    }

    if (date.isBefore(startDate)) {
      return true;
    }

    const start = moment(startDate).startOf('day');
    const end = moment(startDate).add(2, 'days').endOf('day');

    return date.isBetween(start, end, null, '[]');
  };

  const handleCpmBlur = (e) => {
    const value = Number(e.target.value);
    // Use the helper's CPM correction logic
    helpers.handleCpmBlur(value);
  };

  const handleCpmChange = () => {
    // Let Form.Item handle the value update automatically
  };

  useEffect(() => {
    const checkPermission = async () => {
      const authorized = await isAuthorized(['isTrafficker', 'isProcessor'], []);
      setCanEdit(authorized);
    };

    checkPermission();
  }, [isAuthorized]);

  return (
    <>
      <Card id="requester-duration-budget" title="DURATION & BUDGET">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {(() => {
            const today = moment().startOf('day');
            const campaignStartDate = moment(startDate).startOf('day');
            const campaignStatus = currentCampaignOrder?.status;
            
            if (startDate && campaignStartDate.isBefore(today)) {
              // Hide notice if campaign is already live or live_pending
              if (campaignStatus === 'LIVE' || campaignStatus === 'LIVE_PENDING' || campaignStatus === 'LIVE_APPROVED') {
                return null;
              }
              
              return (
                <Alert
                  message="Start Date Notice"
                  description="The campaign start date is in the past."
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              );
            }
            return null;
          })()}

          <Row gutter={20}>
            <Col span={8}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>Campaign Budget</Text>
                <Text type="secondary">Maximum amount campaign spends over duration</Text>
                <Form.Item
                  name="budget"
                  rules={[
                    {
                      required: true,
                      message: 'Please input a budget',
                    },
                    () => ({
                      validator(_, value) {
                        if (value >= 1 || value === '') {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('Please input a valid budget'));
                      },
                    }),
                  ]}
                >
                  <InputNumber
                    addonBefore="$USD"
                    style={{ width: '50%' }}
                    min={0}
                    max={Math.pow(10, 12) - 1}
                    maxLength={12}
                  />
                </Form.Item>
              </Space>
            </Col>
            <Col span={6}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>CPM</Text>
                <Text type="secondary">Rate per 1,000 impressions</Text>
                <Form.Item
                  name="cpm"
                  rules={[
                    {
                      required: true,
                      message: 'Please input a CPM',
                    },
                    () => ({
                      validator(_, value) {
                        if (value >= 0 || value === '') {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('Please input a valid CPM'));
                      },
                    }),
                  ]}
                >
                  <InputNumber
                    style={{ width: '100px' }}
                    min={0}
                    // disabled={currentUserOrg && currentUserOrg.id === advertiserId && !canEdit}
                    maxLength={12}
                    max={Math.pow(10, 12) - 1}
                    onBlur={handleCpmBlur}
                  />
                </Form.Item>
              </Space>
            </Col>
            <Col span={5}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text>Impressions</Text>
                <Text type="secondary">Campaign total</Text>
                <Input
                  style={{ width: '100px' }}
                  disabled
                  value={(derivedState.impressions || 0).toLocaleString()}
                />
              </Space>
            </Col>
          </Row>

          <Row gutter={20}>
            <Col span={24}>
              <Row>
                <Col span={6}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text>Start Date</Text>
                    <Text type="secondary">Leave blank for ASAP</Text>
                    <Form.Item
                      name="startDate"
                      rules={[
                        {
                          required: true,
                          message: 'Please input a start date',
                        },
                      ]}
                    >
                      <DatePicker
                        value={startDate}
                        onChange={handleStartDateChange}
                        disabledDate={(date) =>
                          !canEdit ? date.isBefore(moment().startOf('day')) : false
                        }
                      />
                    </Form.Item>
                  </Space>
                </Col>
                <Col span={6}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Text>End Date</Text>
                    <Text type="secondary">Date campaign will end</Text>
                    <Form.Item
                      name="endDate"
                      rules={[
                        {
                          required: true,
                          message: 'Please input an end date',
                        },
                      ]}
                    >
                      <DatePicker
                        value={endDate}
                        onChange={handleEndDateChange}
                        disabledDate={disabledEndDate}
                      />
                    </Form.Item>
                  </Space>
                </Col>
              </Row>
            </Col>
          </Row>
        </Space>
      </Card>
    </>
  );
};

export default CampaignRequestorBudget;